<!--
Copyright (C) 2026 Checkmk GmbH - License: Checkmk Enterprise License
This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
conditions defined in the file COPYING, which is part of this source code package.
-->

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'

import usei18n from '@/lib/i18n'

import CmkButton from '@/components/CmkButton'
import type { OneColorIcons } from '@/components/CmkIcon/types'
import CmkScrollContainer from '@/components/CmkScrollContainer.vue'
import CmkParagraph from '@/components/typography/CmkParagraph.vue'

import AiAssistantComposer from '@/nonfree/ai/AiAssistantComposer.vue'
import AiAssistantQuestion from '@/nonfree/ai/AiAssistantQuestion.vue'
import AiAssistantWelcomeBanner from '@/nonfree/ai/AiAssistantWelcomeBanner.vue'
import { blockComponent } from '@/nonfree/ai/AiBlock'
import type { Conversation, ConversationError } from '@/nonfree/ai/control-plane/conversation'

const props = defineProps<{
  conversation: Conversation
}>()

const { _t } = usei18n()

const messages = useTemplateRef('messages')
const composer = useTemplateRef('composer')
const followOutput = ref(true)

function describeConversationError(error: ConversationError): string {
  switch (error.kind) {
    case 'incomplete':
      return _t('The answer ended before it was complete.')
    case 'failed':
    case 'request':
      return error.message
  }
}

const meta = computed(() => {
  const error = props.conversation.error.value
  return [
    `id: ${props.conversation.id.value ?? '-'}`,
    `status: ${props.conversation.status.value}`,
    `error: ${error ? describeConversationError(error) : '-'}`
  ].join(' | ')
})

const defaultQuestions: { icon: OneColorIcons; text: string }[] = [
  { icon: 'megaphone', text: _t('What needs my attention right now?') },
  { icon: 'clock', text: _t('What changed in the last hour?') },
  { icon: 'calendar', text: _t('Any recurring problems this week?') },
  { icon: 'wrench', text: _t('What should I do to fix the current problem?') }
]

const showWelcome = computed(
  () => props.conversation.blocks.value.length === 0 && props.conversation.status.value === 'idle'
)

async function send(text: string): Promise<void> {
  followOutput.value = true
  await props.conversation.send(text)
}

function onScroll(): void {
  const container = messages.value?.containerRef
  if (!container) {
    return
  }
  followOutput.value = container.scrollHeight - container.scrollTop - container.clientHeight <= 2
}

function scrollToBottom(): void {
  const container = messages.value?.containerRef
  if (container) {
    container.scrollTop = container.scrollHeight
  }
}

onMounted(scrollToBottom)

watch(
  // The last block is replaced, not changed, while its text streams in.
  () => {
    const blocks = props.conversation.blocks.value
    return [props.conversation, blocks[blocks.length - 1]]
  },
  () => {
    if (followOutput.value) {
      scrollToBottom()
    }
  },
  { flush: 'post' }
)

defineExpose({ focusPrompt: () => composer.value?.focus() })
</script>

<template>
  <div class="ai-assistant-chat">
    <CmkParagraph>{{ meta }}</CmkParagraph>
    <CmkScrollContainer ref="messages" class="ai-assistant-chat__messages" @scroll="onScroll">
      <div v-if="showWelcome" class="ai-assistant-chat__welcome">
        <AiAssistantWelcomeBanner />
        <ul class="ai-assistant-chat__questions" :aria-label="_t('Suggested questions')">
          <li v-for="question in defaultQuestions" :key="question.text">
            <AiAssistantQuestion
              :icon="question.icon"
              :text="question.text"
              @click="conversation.send(question.text)"
            />
          </li>
        </ul>
      </div>
      <component
        :is="blockComponent(block)"
        v-for="(block, index) in conversation.blocks.value"
        :key="index"
        :block="block"
      />
    </CmkScrollContainer>
    <CmkButton v-if="conversation.status.value === 'failed'" @click="conversation.retry()">
      {{ _t('Retry') }}
    </CmkButton>

    <AiAssistantComposer
      ref="composer"
      :streaming="conversation.status.value === 'streaming'"
      :error="conversation.error.value"
      @send="send"
      @stop="conversation.cancel()"
    />
  </div>
</template>

<style scoped>
.ai-assistant-chat {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.ai-assistant-chat__messages {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--dimension-8);
  min-height: 0;
  margin-bottom: var(--dimension-6);
  padding-right: var(--dimension-4);
  font-size: var(--font-size-normal);
}

.ai-assistant-chat__welcome {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100%;
  padding: var(--dimension-6) 0;
  box-sizing: border-box;
  text-align: center;
}

.ai-assistant-chat__questions {
  display: flex;
  flex-direction: column;
  gap: var(--dimension-6);
  align-self: stretch;
  margin: 0;
  padding: 0;
  list-style: none;
}
</style>
